import { Field, Listbox, ListboxButton, ListboxOptions } from "@headlessui/react";
import { ArrowUpRight, Check, ChevronDown, Search } from "lucide-react";
import { cn } from "@/utils/css";
import { TranslatedText } from "@/components/common/text";
import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { kmaToMarket } from "@/utils/markets";
import { useIsDarkTheme } from "@/components/layout/useIsDarkTheme";

export const MultiSelectDropdown = ({
    items = {},
    value = {},
    setValue,
    label,
    className,
    placeholder = "Select",
    emptyLabel,
    minWidth = 239,
    menuWidth,
    themes = [],
    selectAll = false,
    autocomplete = false,
    searchPlaceholder = "",
    usePortal = false,
    selectOnMap,
    showCount = true,
}) => {
    const pxMinWidth = `${minWidth}px`;
    const pxMenuWidth = `${menuWidth}px`;
    const [searchTerm, setSearchTerm] = useState("");
    const prevOpenRef = useRef(false);
    const listboxOptionsRef = useRef(null);
    const [hasScrollbar, setHasScrollbar] = useState(false);

    const checkScrollbar = useCallback(() => {
        if (listboxOptionsRef.current) {
            const { scrollHeight, clientHeight } = listboxOptionsRef.current;
            setHasScrollbar(scrollHeight > clientHeight);
        }
    }, []);

    const handleToggle = (itemId) => {
        const newValue = { ...value };
        if (newValue[itemId]) {
            delete newValue[itemId];
        } else {
            newValue[itemId] = true;
        }
        setValue(newValue);
    };

    const handleSelectAll = () => {
        const newValue = {};
        Object.keys(items).forEach((key) => {
            newValue[key] = true;
        });
        setValue(newValue);
    };

    const handleDeselectAll = () => {
        setValue({});
    };

    const itemsLength = useMemo(() => {
        return items ? Object.keys(items).length : 0;
    }, [items]);

    const filteredItems = useMemo(() => {
        if (!autocomplete || !searchTerm) {
            return items;
        }
        const filtered = {};
        Object.keys(items).forEach((key) => {
            const displayName = (kmaToMarket[key] || key).toLowerCase();
            if (displayName.includes(searchTerm.toLowerCase())) {
                filtered[key] = items[key];
            }
        });
        return filtered;
    }, [items, searchTerm, autocomplete]);

    const getDisplayText = () => {
        const selectedItems = Object.keys(items).filter((key) => value[key]);
        const selectedCount = selectedItems.length;

        if (selectedCount === 0) {
            return emptyLabel ?? placeholder;
        }
        if (selectedCount === itemsLength) {
            return `All (${selectedCount})`;
        }
        return `${selectedCount} selected`;
    };

    const isLabelTop = themes.includes("labelTop");
    const isLoadboard = themes.includes("loadboard");
    const isDarkTheme = useIsDarkTheme();

    // Check if any items are selected
    const areAnyItemsSelected = useMemo(() => {
        return Object.keys(items).some((key) => value[key]);
    }, [items, value]);

    return (
        <div className={isLabelTop ? "flex flex-col gap-2" : "flex flex-row gap-[10px] items-center"}>
            {isLabelTop && label && (
                <TranslatedText className="text-cm-p-text-primary text-[14px] font-medium text-center">{label}</TranslatedText>
            )}
            {!isLabelTop && label && (
                <TranslatedText className={`text-sm font-normal ${isLoadboard ? "text-cm-p-text-primary" : ""}`}>{label}</TranslatedText>
            )}
            <Field
                className={cn("flex flex-col bg-cm-p-surface-container h-[36px] px-4 py-2 rounded-[58px]", className)}
                style={isLoadboard ? { width: pxMinWidth } : { minWidth: pxMinWidth }}
            >
                <Listbox value={null} onChange={() => {}}>
                    {({ open }) => {
                        // Reset search when dropdown closes
                        if (prevOpenRef.current && !open && searchTerm) {
                            setSearchTerm("");
                        }
                        prevOpenRef.current = open;
                        return (
                            <>
                                <ListboxButton className="inline-flex flex-row justify-between items-center gap-1">
                                    <span
                                        className={`text-sm ${isLoadboard ? "text-cm-p-text-primary font-medium" : Object.keys(value).some((key) => value[key]) ? "text-cm-p-text-primary font-medium" : "text-cm-p-text-secondary"} `}
                                    >
                                        {getDisplayText()}
                                    </span>
                                    <ChevronDown
                                        className={cn("opacity-50 text-cm-p-text-primary transition ease-in w-4 h-4", {
                                            "rotate-180": open,
                                        })}
                                    />
                                </ListboxButton>
                                <ListboxOptions
                                    portal={usePortal}
                                    modal={true}
                                    anchor={usePortal ? "bottom start" : undefined}
                                    className="z-40 flex flex-col mt-[18px] rounded-lg bg-cm-p-surface py-[2px] px-[2px] border border-cm-p-outline h-auto overflow-hidden gap-[2px] !max-h-[348px]"
                                    style={{
                                        boxShadow: "0px 4px 14px -3px rgba(93, 93, 93, 0.08)",
                                        minHeight: searchTerm
                                            ? undefined
                                            : `${(itemsLength > 5 ? 5 : itemsLength) * 43 + 4 + (selectAll ? 44 : 0) + (autocomplete ? 44 : 0) + (selectOnMap ? 43 : 0)}px`,
                                        ...(isLoadboard && menuWidth
                                            ? { width: hasScrollbar ? `${parseInt(menuWidth) + 6}px` : pxMenuWidth }
                                            : {}),
                                        marginLeft: isLoadboard && menuWidth ? `${(minWidth - parseInt(menuWidth)) / 2}px` : "0px",
                                    }}
                                >
                                    {autocomplete && (
                                        <div className="relative shrink-0">
                                            <input
                                                type="text"
                                                placeholder={searchPlaceholder}
                                                value={searchTerm}
                                                onChange={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setSearchTerm(e.target.value);
                                                }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }}
                                                className="w-full py-[6px] px-[12px] text-sm rounded-md outline-none focus:outline-none border-none focus:border-none focus:ring-0 bg-cm-p-surface-container text-cm-p-text-secondary placeholder:text-cm-p-text-secondary"
                                            />
                                        </div>
                                    )}
                                    <div
                                        ref={(el) => {
                                            listboxOptionsRef.current = el;
                                            if (el) {
                                                requestAnimationFrame(checkScrollbar);
                                            }
                                        }}
                                        className="flex flex-col gap-[2px] flex-1 min-h-0 overflow-auto"
                                    >
                                        {Object.keys(filteredItems).map((key) => {
                                            const count = items[key];
                                            const isSelected = !!value[key];

                                            return (
                                                <div
                                                    key={key}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleToggle(key);
                                                    }}
                                                    className="inline-flex min-h-[41px] pl-3 pr-2 gap-1 items-center cursor-pointer justify-between hover:bg-cm-p-surface-hover rounded-lg"
                                                >
                                                    <span className="flex-1 min-w-0 truncate text-cm-p-text-primary font-medium text-sm">
                                                        {kmaToMarket[key] || key}
                                                        {showCount ? ` (${count})` : ""}
                                                    </span>
                                                    <div
                                                        className={cn(
                                                            "shrink-0 w-4 h-4 border-2 rounded flex items-center justify-center",
                                                            isLoadboard ? "border-cm-p-accent-500" : "border-cm-p-outline",
                                                            {
                                                                "border-cm-p-accent-500 bg-cm-p-accent-500":
                                                                    isSelected && isLoadboard && selectAll,
                                                            },
                                                            {
                                                                "border-cm-p-accent-500 bg-cm-p-accent-500":
                                                                    isSelected && !(isLoadboard && selectAll),
                                                            },
                                                        )}
                                                    >
                                                        {isSelected && <Check className="size-3 text-cm-p-surface" />}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {Object.keys(filteredItems).length === 0 && autocomplete && searchTerm && (
                                            <div className="px-4 py-3 text-sm text-cm-p-text-secondary text-center">No results found</div>
                                        )}
                                    </div>
                                    {(selectOnMap || selectAll) && !searchTerm ? (
                                        <div className="shrink-0 flex flex-col bg-cm-p-surface">
                                            {selectOnMap ? (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        selectOnMap();
                                                    }}
                                                    className="inline-flex min-h-[41px] m-0 pl-3 pr-2 gap-1 items-center cursor-pointer justify-between hover:bg-cm-p-surface-hover rounded-lg w-full"
                                                >
                                                    <span className="text-cm-p-text-primary font-medium text-sm">Select on map</span>
                                                    <ArrowUpRight className="size-4 text-cm-p-accent-600 shrink-0" />
                                                </button>
                                            ) : null}
                                            {selectAll ? (
                                                <>
                                                    <div className="border-t border-cm-p-outline-variation" />
                                                    <div className="flex flex-row justify-between items-center h-[41px] px-3 gap-4">
                                                        <button
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                handleSelectAll();
                                                            }}
                                                            className={`text-[14px] font-medium cursor-pointer text-cm-p-accent-600 ${areAnyItemsSelected ? "opacity-40" : ""}`}
                                                        >
                                                            Select all
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                handleDeselectAll();
                                                            }}
                                                            className={`text-[14px] font-medium cursor-pointer text-cm-p-accent-600 ${!areAnyItemsSelected ? "opacity-40" : ""}`}
                                                        >
                                                            Deselect all
                                                        </button>
                                                    </div>
                                                </>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </ListboxOptions>
                            </>
                        );
                    }}
                </Listbox>
            </Field>
        </div>
    );
};
